import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  InputAdornment,
  Button,
  IconButton,
  Chip,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  SelectChangeEvent,
  Grid,
  CircularProgress,
  Alert,
  Tooltip,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  Search as SearchIcon,
  PersonAdd as PersonAddIcon,
  FileDownload as FileDownloadIcon,
  MoreVert as MoreVertIcon,
  Email as EmailIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  FilterList as FilterListIcon
} from '@mui/icons-material';
import { collection, query, getDocs, where, orderBy, doc, updateDoc, deleteDoc, onSnapshot, Timestamp, addDoc } from 'firebase/firestore';
import { db } from '../../../../firebase';
import { Subscriber, SubscriptionTier } from '../index';
import { formatCurrency } from '../../../../utils/formatting';

interface SubscribersListProps {
  organizationId: string;
}

const SubscribersList: React.FC<SubscribersListProps> = ({ organizationId }) => {
  // State
  const [subscribers, setSubscribers] = useState<Subscriber[]>([]);
  const [filteredSubscribers, setFilteredSubscribers] = useState<Subscriber[]>([]);
  const [tiers, setTiers] = useState<SubscriptionTier[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSubscriber, setSelectedSubscriber] = useState<Subscriber | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [tierFilter, setTierFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [addSubscriberLoading, setAddSubscriberLoading] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deletingSubscriber, setDeletingSubscriber] = useState(false);
  
  // Menu state
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(anchorEl);
  
  // New subscriber form data
  const [newSubscriber, setNewSubscriber] = useState({
    userId: '',
    userName: '',
    userEmail: '',
    tierId: '',
    isAnnual: false,
    paymentStatus: 'active'
  });
  
  // Fetch subscribers and tiers on component mount
  useEffect(() => {
    fetchSubscribersAndTiers();
  }, [organizationId]);
  
  // Update filtered subscribers when filter changes
  useEffect(() => {
    filterSubscribers();
  }, [subscribers, searchTerm, tierFilter, statusFilter]);
  
  // Fetch subscribers and tiers data
  const fetchSubscribersAndTiers = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Fetch tiers first
      const tiersRef = collection(db, `organizations/${organizationId}/subscriptionTiers`);
      const tiersSnapshot = await getDocs(tiersRef);
      
      const fetchedTiers: SubscriptionTier[] = tiersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as SubscriptionTier));
      
      setTiers(fetchedTiers);
      
      // Fetch subscribers
      const subscribersRef = collection(db, `organizations/${organizationId}/subscribers`);
      const subscribersQuery = query(subscribersRef, orderBy('subscribedDate', 'desc'));
      
      // Use onSnapshot for real-time updates
      const unsubscribe = onSnapshot(subscribersQuery, (snapshot) => {
        const fetchedSubscribers: Subscriber[] = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        } as Subscriber));
        
        setSubscribers(fetchedSubscribers);
        setLoading(false);
      }, (err) => {
        console.error('Error fetching subscribers:', err);
        setError('Failed to load subscribers. Please try again.');
        setLoading(false);
      });
      
      // Cleanup subscription on unmount
      return () => unsubscribe();
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to load data. Please try again.');
      setLoading(false);
    }
  };
  
  // Filter subscribers based on search term and filters
  const filterSubscribers = () => {
    let filtered = [...subscribers];
    
    // Apply search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        sub => 
          sub.userName.toLowerCase().includes(term) || 
          sub.userEmail.toLowerCase().includes(term)
      );
    }
    
    // Apply tier filter
    if (tierFilter !== 'all') {
      filtered = filtered.filter(sub => sub.tierId === tierFilter);
    }
    
    // Apply status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(sub => sub.paymentStatus === statusFilter);
    }
    
    setFilteredSubscribers(filtered);
  };
  
  // Handle search input change
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };
  
  // Handle tier filter change
  const handleTierFilterChange = (e: SelectChangeEvent) => {
    setTierFilter(e.target.value as string);
  };
  
  // Handle status filter change
  const handleStatusFilterChange = (e: SelectChangeEvent) => {
    setStatusFilter(e.target.value as string);
  };
  
  // Handle pagination
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };
  
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  
  // Handle menu open
  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, subscriber: Subscriber) => {
    setAnchorEl(event.currentTarget);
    setSelectedSubscriber(subscriber);
  };
  
  // Handle menu close
  const handleMenuClose = () => {
    setAnchorEl(null);
  };
  
  // Open delete confirmation dialog
  const handleDeleteDialogOpen = () => {
    setShowDeleteDialog(true);
    handleMenuClose();
  };
  
  // Cancel subscriber deletion
  const handleDeleteCancel = () => {
    setShowDeleteDialog(false);
  };
  
  // Confirm subscriber deletion
  const handleDeleteConfirm = async () => {
    if (!selectedSubscriber) return;
    
    try {
      setDeletingSubscriber(true);
      
      // Delete the subscriber
      const subscriberRef = doc(db, `organizations/${organizationId}/subscribers/${selectedSubscriber.id}`);
      await deleteDoc(subscriberRef);
      
      setShowDeleteDialog(false);
      setSelectedSubscriber(null);
    } catch (err) {
      console.error('Error deleting subscriber:', err);
      setError('Failed to delete subscriber. Please try again.');
    } finally {
      setDeletingSubscriber(false);
    }
  };
  
  // Export subscribers to CSV
  const handleExportCSV = () => {
    // Create CSV content
    const headers = ['Name', 'Email', 'Tier', 'Start Date', 'Renewal Date', 'Status', 'Payment Type', 'Amount'];
    const rows = filteredSubscribers.map(sub => [
      sub.userName,
      sub.userEmail,
      tiers.find(t => t.id === sub.tierId)?.name || 'Unknown',
      new Date(sub.subscribedDate.seconds * 1000).toLocaleDateString(),
      new Date(sub.renewalDate.seconds * 1000).toLocaleDateString(),
      sub.paymentStatus,
      sub.isAnnual ? 'Annual' : 'Monthly',
      sub.amount.toFixed(2)
    ]);
    
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');
    
    // Create and download the file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `subscribers-${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  
  // New subscriber form handlers
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setNewSubscriber({
      ...newSubscriber,
      [name]: value
    });
  };
  
  const handleSelectChange = (e: SelectChangeEvent) => {
    const name = e.target.name as string;
    const value = e.target.value;
    
    setNewSubscriber({
      ...newSubscriber,
      [name]: name === 'isAnnual' ? value === 'true' : value
    });
  };
  
  const handleCloseAddDialog = () => {
    setShowAddDialog(false);
    // Reset form data
    setNewSubscriber({
      userId: '',
      userName: '',
      userEmail: '',
      tierId: '',
      isAnnual: false,
      paymentStatus: 'active'
    });
  };
  
  const handleAddSubscriber = async () => {
    // Form validation
    if (!newSubscriber.userName || !newSubscriber.userEmail || !newSubscriber.tierId) {
      setError('Please fill in all required fields');
      return;
    }
    
    try {
      setAddSubscriberLoading(true);
      
      // Get selected tier data
      const selectedTier = tiers.find(tier => tier.id === newSubscriber.tierId);
      
      if (!selectedTier) {
        setError('Selected subscription tier not found');
        setAddSubscriberLoading(false);
        return;
      }
      
      // Calculate amount based on monthly/annual selection
      const amount = newSubscriber.isAnnual ? selectedTier.annualPrice : selectedTier.monthlyPrice;
      
      // Calculate renewal date
      const now = new Date();
      const renewalDate = new Date(now);
      renewalDate.setMonth(renewalDate.getMonth() + (newSubscriber.isAnnual ? 12 : 1));
      
      // Create new subscriber object
      const subscriberData: Omit<Subscriber, 'id'> = {
        userId: newSubscriber.userId || `manual-${Date.now()}`, // Generate ID if not provided
        userName: newSubscriber.userName,
        userEmail: newSubscriber.userEmail,
        tierId: newSubscriber.tierId,
        tierName: selectedTier.name,
        subscribedDate: Timestamp.now(),
        renewalDate: Timestamp.fromDate(renewalDate),
        paymentStatus: newSubscriber.paymentStatus as 'active' | 'past_due' | 'canceled' | 'trial',
        isAnnual: newSubscriber.isAnnual,
        amount
      };
      
      // Add to Firestore
      const subscribersRef = collection(db, `organizations/${organizationId}/subscribers`);
      await addDoc(subscribersRef, subscriberData);
      
      // Close dialog and reset form
      handleCloseAddDialog();
    } catch (err) {
      console.error('Error adding subscriber:', err);
      setError('Failed to add subscriber. Please try again.');
    } finally {
      setAddSubscriberLoading(false);
    }
  };
  
  // Render status chip
  const renderStatusChip = (status: string) => {
    switch (status) {
      case 'active':
        return <Chip label="Active" color="success" size="small" />;
      case 'past_due':
        return <Chip label="Past Due" color="warning" size="small" />;
      case 'canceled':
        return <Chip label="Canceled" color="error" size="small" />;
      case 'trial':
        return <Chip label="Trial" color="info" size="small" />;
      default:
        return <Chip label={status} size="small" />;
    }
  };
  
  // Empty state
  if (!loading && subscribers.length === 0) {
    return (
      <Paper sx={{ p: 3, textAlign: 'center' }}>
        <Box sx={{ py: 4 }}>
          <Typography variant="h6" gutterBottom>
            No Subscribers Yet
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            You don't have any subscribers yet. Create subscription tiers and share your page to start growing your membership.
          </Typography>
          <Button
            variant="contained"
            startIcon={<PersonAddIcon />}
            onClick={() => setShowAddDialog(true)}
          >
            Add Subscriber Manually
          </Button>
        </Box>
      </Paper>
    );
  }
  
  return (
    <>
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      
      {/* Filters and actions */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              placeholder="Search by name or email"
              value={searchTerm}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel id="tier-filter-label">Tier</InputLabel>
              <Select
                labelId="tier-filter-label"
                value={tierFilter}
                label="Tier"
                onChange={handleTierFilterChange}
              >
                <MenuItem value="all">All Tiers</MenuItem>
                {tiers.map((tier) => (
                  <MenuItem key={tier.id} value={tier.id}>
                    {tier.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel id="status-filter-label">Status</InputLabel>
              <Select
                labelId="status-filter-label"
                value={statusFilter}
                label="Status"
                onChange={handleStatusFilterChange}
              >
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="past_due">Past Due</MenuItem>
                <MenuItem value="canceled">Canceled</MenuItem>
                <MenuItem value="trial">Trial</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={4} sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<FileDownloadIcon />}
              onClick={handleExportCSV}
              disabled={filteredSubscribers.length === 0}
            >
              Export
            </Button>
            
            <Button
              variant="contained"
              color="primary"
              startIcon={<PersonAddIcon />}
              onClick={() => setShowAddDialog(true)}
            >
              Add Subscriber
            </Button>
          </Grid>
        </Grid>
      </Paper>
      
      {/* Subscribers table */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 5 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Paper>
          <TableContainer>
            <Table sx={{ minWidth: 650 }} aria-label="subscribers table">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Subscription</TableCell>
                  <TableCell>Start Date</TableCell>
                  <TableCell>Renewal</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Amount</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredSubscribers
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((subscriber) => {
                    const tier = tiers.find(t => t.id === subscriber.tierId);
                    
                    return (
                      <TableRow key={subscriber.id}>
                        <TableCell component="th" scope="row">
                          {subscriber.userName}
                        </TableCell>
                        <TableCell>{subscriber.userEmail}</TableCell>
                        <TableCell>
                          {tier?.name || 'Unknown'}
                          <Typography variant="caption" display="block" color="text.secondary">
                            {subscriber.isAnnual ? 'Annual' : 'Monthly'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {new Date(subscriber.subscribedDate.seconds * 1000).toLocaleDateString()}
                        </TableCell>
                        <TableCell>
                          {new Date(subscriber.renewalDate.seconds * 1000).toLocaleDateString()}
                        </TableCell>
                        <TableCell>
                          {renderStatusChip(subscriber.paymentStatus)}
                        </TableCell>
                        <TableCell>
                          {formatCurrency(subscriber.amount)}
                          <Typography variant="caption" display="block" color="text.secondary">
                            {subscriber.isAnnual ? '/year' : '/month'}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <IconButton
                            size="small"
                            onClick={(e) => handleMenuClick(e, subscriber)}
                          >
                            <MoreVertIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                
                {filteredSubscribers.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 3 }}>
                      <Typography variant="body1" color="text.secondary">
                        No subscribers found
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={filteredSubscribers.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Paper>
      )}
      
      {/* Action menu */}
      <Menu
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleMenuClose}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit</ListItemText>
        </MenuItem>
        
        <MenuItem onClick={handleMenuClose}>
          <ListItemIcon>
            <EmailIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Email</ListItemText>
        </MenuItem>
        
        <MenuItem onClick={handleDeleteDialogOpen}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Remove</ListItemText>
        </MenuItem>
      </Menu>
      
      {/* Add subscriber dialog */}
      <Dialog
        open={showAddDialog}
        onClose={handleCloseAddDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add Subscriber Manually</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                name="userName"
                label="Name"
                fullWidth
                value={newSubscriber.userName}
                onChange={handleInputChange}
                required
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                name="userEmail"
                label="Email"
                type="email"
                fullWidth
                value={newSubscriber.userEmail}
                onChange={handleInputChange}
                required
              />
            </Grid>
            
            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel id="tier-select-label">Subscription Tier</InputLabel>
                <Select
                  labelId="tier-select-label"
                  name="tierId"
                  value={newSubscriber.tierId}
                  label="Subscription Tier"
                  onChange={handleSelectChange}
                >
                  {tiers.map((tier) => (
                    <MenuItem key={tier.id} value={tier.id}>
                      {tier.name} - {formatCurrency(tier.monthlyPrice)}/month
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel id="billing-cycle-label">Billing Cycle</InputLabel>
                <Select
                  labelId="billing-cycle-label"
                  name="isAnnual"
                  value={newSubscriber.isAnnual ? 'true' : 'false'}
                  label="Billing Cycle"
                  onChange={handleSelectChange}
                >
                  <MenuItem value="false">Monthly</MenuItem>
                  <MenuItem value="true">Annual</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel id="status-select-label">Status</InputLabel>
                <Select
                  labelId="status-select-label"
                  name="paymentStatus"
                  value={newSubscriber.paymentStatus}
                  label="Status"
                  onChange={handleSelectChange}
                >
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="past_due">Past Due</MenuItem>
                  <MenuItem value="canceled">Canceled</MenuItem>
                  <MenuItem value="trial">Trial</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAddDialog}>Cancel</Button>
          <Button
            onClick={handleAddSubscriber}
            variant="contained"
            color="primary"
            disabled={addSubscriberLoading}
            startIcon={addSubscriberLoading ? <CircularProgress size={20} /> : <PersonAddIcon />}
          >
            Add Subscriber
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Delete confirmation dialog */}
      <Dialog
        open={showDeleteDialog}
        onClose={handleDeleteCancel}
      >
        <DialogTitle>Remove Subscriber</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to remove {selectedSubscriber?.userName} from your subscribers? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} disabled={deletingSubscriber}>
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteConfirm} 
            color="error" 
            disabled={deletingSubscriber}
            startIcon={deletingSubscriber ? <CircularProgress size={20} /> : <DeleteIcon />}
          >
            Remove
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SubscribersList;
